import { useQuery } from '@tanstack/react-query';
import { audienceApi } from './audienceApi';
import type { AudiencePeriod, AudiencePlatform } from '../model/types';

export const audienceQueryKeys = {
  all: ['audience'] as const,
  overview: (assistantId: string) =>
    [...audienceQueryKeys.all, 'overview', assistantId] as const,
  growth: (assistantId: string, platform: string, period: string) =>
    [...audienceQueryKeys.all, 'growth', assistantId, platform, period] as const,
  users: (
    assistantId: string,
    platform: string,
    period: string,
    search: string,
    offset: number,
  ) =>
    [
      ...audienceQueryKeys.all,
      'users',
      assistantId,
      platform,
      period,
      search,
      offset,
    ] as const,
};

// Не ретраить на auth-ошибках (как в statistics/billing).
const retry = (failureCount: number, error: unknown) => {
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 401 || status === 403) return false;
  return failureCount < 1;
};

export function useAudienceOverview(assistantId: string) {
  return useQuery({
    queryKey: audienceQueryKeys.overview(assistantId),
    queryFn: () => audienceApi.getOverview(assistantId),
    staleTime: 10_000,
    retry,
  });
}

export function useAudienceGrowth(
  assistantId: string,
  platform: AudiencePlatform,
  period: AudiencePeriod,
) {
  return useQuery({
    queryKey: audienceQueryKeys.growth(assistantId, platform, period),
    queryFn: () => audienceApi.getGrowth(assistantId, platform, period),
    staleTime: 10_000,
    retry,
  });
}

export function useAudienceUsers(
  assistantId: string,
  platform: AudiencePlatform,
  period: AudiencePeriod,
  search: string,
  offset: number,
  limit = 50,
) {
  return useQuery({
    queryKey: audienceQueryKeys.users(
      assistantId,
      platform,
      period,
      search,
      offset,
    ),
    queryFn: () =>
      audienceApi.getUsers(assistantId, platform, period, search || undefined, limit, offset),
    staleTime: 10_000,
    retry,
  });
}
